
import { Options, prop, Vue } from "vue-class-component";
import {
  PieceBookingV4,
  ErrorBookingV4,
  HwsThresholdConfig,
  ErrorPiecesBookingV4,
  CreateBookingV4
} from "@/domain/entities/BookingV4";
import { formatInputNoDecimal } from "@/app/infrastructures/misc/common-library/FormatInputNoDecimal";
import { maxLengthWeight } from "../../../create-booking-v2/components/modules";
import absolutePosition from "@/app/infrastructures/misc/common-library/dom/absolutePosition";
import { error } from "console";
import {
  formatByCountry,
  formatInputByCountry,
  parseByCountry
} from "@/app/infrastructures/misc/Utils";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { SttAdjustmentRule } from "@/domain/entities/ShipmentBooking";
import { ConfigValidationForm } from "@/domain/entities/CreateBookingV3";

type DimensionField = "length" | "width" | "height";

class Props {
  modelValue = prop<PieceBookingV4[]>({ required: true, default: () => [] });
  form = prop<CreateBookingV4>({
    default: new CreateBookingV4(),
    type: Object
  });
  errors = prop<ErrorBookingV4>({
    required: true,
    default: () => new ErrorBookingV4(),
    type: Object
  });
  volumeWeightDivisor = prop<number>({ required: true });
  hwsConfig = prop<HwsThresholdConfig>({
    default: new HwsThresholdConfig()
  });
  maxPieces = prop<number>({ default: 15, type: Number });
  singlePieceOnly = prop<boolean>({ default: false, type: Boolean });
  disableAddPiece = prop<boolean>({ default: false, type: Boolean });
  showSubKgHelper = prop<boolean>({ default: false, type: Boolean });
  useDefaultDimensions = prop<boolean>({ default: true, type: Boolean });
  numericLocale = prop<"id" | "my">({ default: "id" });
  // Adjust v4 per-field rules (GENESIS-20182) — optional, default EDITABLE
  configGrossWeight = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configDimension = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configAdjustPieceAdd = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  configAdjustPieceRemove = prop<SttAdjustmentRule>({
    default: new SttAdjustmentRule(),
    type: Object
  });
  isShowSectionUpload = prop<boolean>({
    default: false,
    type: Boolean
  });
  disabledWeightAdjustment = prop<boolean>({
    default: false,
    type: Boolean
  });
  minWeightThreshold = prop<number>({
    default: 0,
    type: Number
  });
  isEdit = prop<boolean>({
    default: false,
    type: Boolean
  });
  isMaxChWeight = prop<boolean>({
    default: false,
    type: Boolean
  });
  preventDecreasedWeight = prop<boolean>({
    default: true,
    type: Boolean
  });
  eligibleEditSize = prop<boolean>({
    default: false
  });
}

@Options({
  name: "PackageWeightSectionV4",
  emits: [
    "update:modelValue",
    "validationChange",
    "grossWeightChange",
    "tariffContextChange"
  ],
  watch: {
    modelValue: {
      deep: true,
      immediate: true,
      handler(newValue) {
        if (!this.isInitialWeightSet && newValue && newValue.length > 0) {
          this.initialWeights = newValue.map(
            (piece: { grossWeight: any }) => piece.grossWeight
          );
          this.initialLength = newValue.map(
            (piece: { length: any }) => piece.length
          );
          this.initialWidth = newValue.map(
            (piece: { width: any }) => piece.width
          );
          this.initialHeight = newValue.map(
            (piece: { height: any }) => piece.height
          );
          this.isInitialWeightSet = true;
        }
        this.emitValidationChange();
      }
    },
    singlePieceOnly() {
      this.emitValidationChange();
    },
    volumeWeightDivisor: {
      immediate: true,
      handler() {
        this.recalcAllVolumeWeights();
      }
    },
    disabledWeightAdjustment: {
      deep: true,
      handler(prevVal, newVal) {
        // reset GW and VW
        if (prevVal && !newVal) {
          this.resetGrossweightToInitial()
        }
      }
    },
    isMaxChWeight() {
      this.emitValidationChange();
    },
  }
})
export default class PackageWeightSectionV4 extends Vue.with(Props) {
  maxKg = 400;
  isWeightTouched = false;
  initialWeights: number[] = [];
  initialLength: number[] = [];
  initialHeight: number[] = [];
  initialWidth: number[] = [];
  isInitialWeightSet = false;

  get isInternalAccount() {
    return AccountController.accountData.isInternalAccount;
  }

  get isClientAccount() {
    return AccountController.accountData.isAllClient;
  }

  get isOnePieceRestriction(): boolean {
    return this.singlePieceOnly && this.modelValue.length > 1;
  }

  get disabledGrossWeight(): boolean {
    const isPhotoLocked =
      this.isShowSectionUpload && this.disabledWeightAdjustment;
    return (
      isPhotoLocked ||
      this.configGrossWeight.rule.includes(ConfigValidationForm.NON_EDITABLE) ||
      this.disabledWeightAdjustment
    );
  }

  get disabledDimension(): boolean {
    const isPhotoLocked =
      this.isShowSectionUpload && this.disabledWeightAdjustment;
    return (
      isPhotoLocked ||
      this.configDimension.rule.includes(ConfigValidationForm.NON_EDITABLE) ||
      this.disabledWeightAdjustment
    );
  }

  get totalCurrentChargeableWeight(): number {
    return (this.modelValue || []).reduce((acc, piece) => {
      const gross = Number(piece.grossWeight) || 0;
      return acc + gross;
    }, 0);
  }

  get isWeightLessThanThreshold(): boolean {
    if (!this.isWeightTouched) return false;
    if (this.disabledGrossWeight && this.disabledDimension) return false;
    if (this.isInternalAccount) return false;
    if (!this.minWeightThreshold || this.minWeightThreshold <= 0) return false;
    const hasInputtedWeight = (this.modelValue || []).some(
      (piece) => piece.grossWeight > 0 || piece.length > 0
    );
    if (!hasInputtedWeight) return false;
    return this.totalCurrentChargeableWeight < this.minWeightThreshold;
  }

  get canAddPiece(): boolean {
    return (
      this.configAdjustPieceAdd.rule === ConfigValidationForm.EDITABLE &&
      !this.disabledWeightAdjustment
    );
  }

  get canRemovePiece(): boolean {
    return (
      this.configAdjustPieceRemove.rule === ConfigValidationForm.EDITABLE &&
      !this.disabledWeightAdjustment
    );
  }

  get isMaxTotalGrossweight() {
    return (
      this.modelValue.reduce((prev, curr) => {
        return prev + curr.grossWeight;
      }, 0) > this.maxKg
    );
  }

  get isMaxTotalVolumeweight() {
    return (
      this.modelValue.reduce((prev, curr) => {
        return prev + curr.volumeWeight;
      }, 0) > this.maxKg
    );
  }

  mounted() {
    if (!this.modelValue.length) {
      this.modelValue.push(this.newPiece());
      this.emitModelValue();
    }
  }

  captureInitialValues(values: PieceBookingV4[]) {
    this.initialWeights = values.map((piece) => piece.grossWeight);
    this.initialLength = values.map((piece) => piece.length);
    this.initialWidth = values.map((piece) => piece.width);
    this.initialHeight = values.map((piece) => piece.height);
    this.isInitialWeightSet = true;
  }

  newPiece(): PieceBookingV4 {
    const length = this.useDefaultDimensions ? 3 : 0;
    const width = this.useDefaultDimensions ? 20 : 0;
    const height = this.useDefaultDimensions ? 10 : 0;
    return new PieceBookingV4({
      length,
      width,
      height,
      grossWeight: 0,
      volumeWeight: this.computeVolumeWeight(length, width, height),
      error: new ErrorPiecesBookingV4()
    });
  }

  computeVolumeWeight(length: number, width: number, height: number): number {
    if (!this.volumeWeightDivisor) return 0;
    return (length * width * height) / this.volumeWeightDivisor;
  }

  dimensionInputValue(value: number): number | string {
    return this.useDefaultDimensions ? value : value || "";
  }

  recalcAllVolumeWeights() {
    this.modelValue.forEach((piece) => {
      piece.volumeWeight = this.computeVolumeWeight(
        piece.length,
        piece.width,
        piece.height
      );
    });
    this.emitModelValue();
  }

  emitModelValue() {
    this.$emit("update:modelValue", this.modelValue);
  }

  emitValidationChange() {
    const isValid =
      this.modelValue.length > 0 &&
      !this.isOnePieceRestriction &&
      this.modelValue.every((piece) => !this.rowInvalid(piece)) &&
      !this.isMaxChWeight;
    this.$emit("validationChange", isValid);
  }

  rowInvalid(piece: PieceBookingV4): boolean {
    return (
      !(piece.grossWeight > 0) ||
      !(piece.length > 0) ||
      !(piece.width > 0) ||
      !(piece.height > 0) ||
      piece.error.errorHws ||
      piece.error.errorMaximumGrossWeight ||
      piece.error.errorMandatoryGrossWeight ||
      piece.error.errorMandatoryVolumeWeight ||
      piece.error.errorDecreasedWeight
    );
  }

  get hasErrorHws() {
    return this.modelValue.some((piece) => {
      return !!piece.error.errorHws
    });
  }

  // Raw in-progress text per row index, kept while the field has focus so a
  // trailing/incidental separator (e.g. "12,") isn't snapped away by the
  // parsed-number round trip on every keystroke. Cleared on blur.
  grossWeightDrafts: Record<number, string> = {};

  displayGrossWeight(index: number): string {
    const draft = this.grossWeightDrafts[index];
    return draft !== undefined
      ? draft
      : this.formatWeightInput(this.modelValue[index].grossWeight);
  }

  onGrossWeightBlur(index: number) {
    delete this.grossWeightDrafts[index];
    this.modelValue[index].error.errorMandatoryGrossWeight =
      this.modelValue[index].grossWeight === 0;
  }

  formatWeightInput(raw: string | number): string {
    return formatInputByCountry(raw, {
      type: "weight",
      country: this.accountCountryCode
    });
  }

  grossWeightMaxLength(index: number): number {
    return maxLengthWeight(
      this.displayGrossWeight(index),
      this.accountCountryCode
    );
  }

  parseWeightValue(formatted: string): number {
    return parseByCountry(formatted, {
      country: this.accountCountryCode
    });
  }

  get accountCountryCode() {
    return this.numericLocale.toUpperCase();
  }

  formatWeightDisplay(value: number): string {
    return formatByCountry(value, {
      type: "weight",
      country: this.accountCountryCode
    });
  }

  formatDimensionDisplay(value: number): string {
    const str = value.toString();
    return this.accountCountryCode === "MY" ? str : str.replace(".", ",");
  }

  onGrossWeightInput(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const formatted = this.formatWeightInput(input.value);
    input.value = formatted;
    this.grossWeightDrafts[index] = formatted;

    const newWeight = this.parseWeightValue(formatted);
    this.modelValue[index].grossWeight = newWeight;

    if (this.isEdit && !this.isInternalAccount && !this.isClientAccount) {
      const initialWeight = this.initialWeights[index] ?? newWeight;
      this.modelValue[index].error.errorDecreasedWeight =
        this.preventDecreasedWeight && newWeight < initialWeight;
    } else {
      this.modelValue[index].error.errorDecreasedWeight = false;
    }

    this.modelValue[index].error.errorMandatoryGrossWeight = newWeight === 0;
    this.modelValue[index].error.errorHws =
      this.rowHwsErrors[index] && this.modelValue.length > 1;
    this.modelValue[index].error.errorMaximumGrossWeight =
      newWeight > this.maxKg;

    this.emitModelValue();
    this.$emit("tariffContextChange");
    this.$emit("grossWeightChange");
  }

  onDimensionInput(event: Event, index: number, field: DimensionField) {
    const input = event.target as HTMLInputElement;
    const sanitized = formatInputNoDecimal(input.value).replace(/\D/g, "");

    // Sync the DOM input value to remove any typed/pasted non-numeric characters
    input.value = sanitized;

    const value = Number(sanitized) || 0;
    this.modelValue[index][field] = value;

    this.modelValue[index].volumeWeight = this.computeVolumeWeight(
      this.modelValue[index].length,
      this.modelValue[index].width,
      this.modelValue[index].height
    );

    this.modelValue[index].error.errorMandatoryVolumeWeight =
      this.modelValue[index].length === 0 ||
      this.modelValue[index].width === 0 ||
      this.modelValue[index].height === 0;

    this.emitModelValue();
    this.$emit("tariffContextChange");
  }

  async addPiece() {
    if (this.singlePieceOnly) return;
    if (this.disableAddPiece) return;
    if (this.modelValue.length >= this.maxPieces) return;
    if (this.showHwsAlert) return;
    this.modelValue.push(this.newPiece());
    this.emitModelValue();
    this.$emit("tariffContextChange");
    await this.$nextTick();
    const inputs = document.querySelectorAll<HTMLInputElement>(
      '[data-testid^="booking-v4-package-weight-input-gross-weight-"]'
    );
    inputs[inputs.length - 1]?.focus();
  }

  removePiece(index: number) {
    if (this.modelValue.length <= 1) return;
    this.modelValue.splice(index, 1);
    // Drafts are keyed by index, which shifts after splice — drop them all
    // rather than risk a stale draft leaking onto the wrong row.
    this.grossWeightDrafts = {};
    if (this.modelValue.length === 1) {
      this.modelValue[0].error.errorHws = false;
    }
    this.emitModelValue();
    this.$emit("tariffContextChange");
  }

  exceedsHwsThreshold(piece: PieceBookingV4): boolean {
    if (!this.hwsConfig.minimumExist) return false;
    return (
      piece.grossWeight >= this.hwsConfig.minimumWeightKg
    );
  }

  get showHwsAlert(): boolean {
    const first = this.modelValue[0];
    return (
      !!first &&
      this.hwsConfig.minimumExist &&
      first.grossWeight >= this.hwsConfig.minimumWeightKg
    );
  }

  get rowHwsErrors(): boolean[] {
    return this.modelValue.map((piece) => this.exceedsHwsThreshold(piece));
  }

  get tooltipStyle() {
    return {
      fontFamily: "Montserrat",
      width: "400px",
      backgroundColor: "#4D4D4D",
      color: "#FFFFFF",
      fontSize: "12px",
      marginTop: "16px",
      visibility: "hidden"
    };
  }

  async hideTooltip(event: any) {
    const refs: any = this.$refs;
    refs["package-weight-tooltip"].hide(event);
  }

  async toggleTooltip(event: any) {
    const refs: any = this.$refs;
    refs["package-weight-tooltip"].show(event);
    await this.$nextTick();
    const container = document.getElementById(
      "package-weight-tooltip-container"
    );
    if (!container) return;
    absolutePosition(
      container,
      event.currentTarget,
      refs["package-weight-tooltip"].visible,
      "center"
    );
  }

  get packageWeightTooltipContent() {
    const content = AccountController.accountData.accountIsForeign
      ? [this.$t("bookingV4.goods.koli.tooltip.line1")]
      : [
          this.$t("bookingV4.goods.koli.tooltip.line1"),
          this.$t("bookingV4.goods.koli.tooltip.line2"),
          this.$t("bookingV4.goods.koli.tooltip.line3")
        ];
    return {
      title: this.$t("bookingV4.goods.koli.tooltip.title"),
      content: content
    };
  }

  resetGrossweightToInitial() {
    this.modelValue.map((piece, index) => {
      piece.grossWeight = this.initialWeights[index];
      piece.height = this.initialHeight[index];
      piece.width = this.initialWidth[index];
      piece.length = this.initialLength[index];
      return piece
    });
  }

  enableEditSizeBasedOnWeight(grossWeight: number) {
    const eligibleWeight = 10; // kgs
    return !this.eligibleEditSize || grossWeight >= eligibleWeight;
  }
}
